/* The ticketing service. Both endpoints send permissive CORS headers, so the
   browser talks to them directly — nothing is proxied, and the password never
   leaves this tab: only the token it returns is kept, in sessionStorage. */

const BASE = 'https://digitalday.sinarmas.com/ticket';
export const AUTH_URL = `${BASE}/v1/tenant/auth`;
export const LIST_URL = `${BASE}/v2/ticket`;

/* This event. The service still holds last year's DAY12025 tickets, and they
   outnumber ours three to one, so every read is pinned to this. */
export const SKU = 'DAY12026';

/* The service's own `status_ticket` codes, and the string the list endpoint
   filters on. Only stage 4 has a qr_string — a ticket has no QR until its guest
   has said they are coming — so that is what a print run is normally for. */
export const STAGES = [
  { code: '4', param: 'CLAIMED', label: 'Confirmed — coming', hasQr: true },
  { code: '3', param: 'INVITED', label: 'Invited, no reply yet', hasQr: false },
  { code: '2', param: 'APPROVED', label: 'Approved', hasQr: false },
  { code: '1', param: 'DRAFT', label: 'Draft', hasQr: false },
];
export const stageOf = row => STAGES.find(s => s.code === String(row.status_ticket));

/* The session lives in localStorage, not sessionStorage: a registration desk
   reloads the page, closes the lid and comes back, and being thrown to the
   login screen each time is worse than useless. The access token lasts five
   hours and that is the whole session — the thirty-day refresh token is
   deliberately NOT kept, because there is no confirmed endpoint to spend it on
   and an unusable admin credential sitting on a shared desk machine is only a
   liability. Signing out clears everything. */
const TOKEN_KEY = 'smdd.token';
const EMAIL_KEY = 'smdd.email';

/* Every call this app makes to the ticketing service, counted per day.

   The quota is shared with check-in, so "how much are we using" needs an answer
   from the meter rather than from reasoning about the code. Kept on the device,
   because that is where the calls are made from; a desk sees its own. */
const CALLS_KEY = 'smdd.calls';
const today = () => new Date().toISOString().slice(0, 10);

export function countCall(kind) {
  try {
    const log = JSON.parse(localStorage.getItem(CALLS_KEY) || '{}');
    const day = log[today()] || {};
    day[kind] = (day[kind] || 0) + 1;
    log[today()] = day;
    // A fortnight is plenty to answer "is today unusual".
    const keep = Object.keys(log).sort().slice(-14);
    localStorage.setItem(CALLS_KEY,
      JSON.stringify(Object.fromEntries(keep.map(d => [d, log[d]]))));
  } catch { /* storage full or blocked: the counter is not worth failing over */ }
}

export function callLog() {
  try { return JSON.parse(localStorage.getItem(CALLS_KEY) || '{}'); }
  catch { return {}; }
}

export function callsToday() {
  const day = callLog()[today()] || {};
  return Object.values(day).reduce((a, b) => a + b, 0);
}

export function getToken() { return localStorage.getItem(TOKEN_KEY) || ''; }
export function getEmail() { return localStorage.getItem(EMAIL_KEY) || ''; }
export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(EMAIL_KEY);
  sessionStorage.removeItem(TOKEN_KEY);   // tidy up the old location
  sessionStorage.removeItem(EMAIL_KEY);
}

/** Seconds until the stored token expires, or 0 if there isn't a usable one. */
export function sessionRemaining() {
  const token = getToken();
  if (!token) return 0;
  try {
    const [, payload] = token.split('.');
    const claims = JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
    return Math.max(0, (claims.exp || 0) * 1000 - Date.now()) / 1000;
  } catch {
    return 0;              // unreadable token is no token
  }
}

/* Checking expiry here rather than waiting for a 401 means the login screen
   appears before a half-loaded roster does. */
export const sessionValid = () => sessionRemaining() > 60;

export async function login(email, password) {
  countCall('sign in');
  const r = await fetch(AUTH_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if ([400, 401, 403].includes(r.status)) {
    throw new Error('That email and password were rejected.');
  }
  if (!r.ok) throw new Error(`Sign-in failed (${r.status}).`);
  const body = await r.json();
  if (!body.access_token) throw new Error('No access token came back.');
  localStorage.setItem(TOKEN_KEY, body.access_token);
  localStorage.setItem(EMAIL_KEY, email);
  return body.access_token;
}

export class Unauthorised extends Error {}

/* Optional. The ticket service has no endpoint that turns a scanned qr_string
   back into a ticket, and the string is re-encrypted on every read, so a QR the
   guest was emailed cannot be matched offline. If your team adds one, put its
   URL in here (or on the scan desk) with {code} where the scanned string goes,
   and the desk will use it whenever a local match fails. It must be read-only. */
const RESOLVE_KEY = 'smdd.resolveUrl';
export const getResolveUrl = () => localStorage.getItem(RESOLVE_KEY) || '';
export const setResolveUrl = u => u
  ? localStorage.setItem(RESOLVE_KEY, u) : localStorage.removeItem(RESOLVE_KEY);

/** Ask that endpoint what ticket a scanned code belongs to. */
export async function resolveCode(code) {
  const template = getResolveUrl();
  if (!template) return '';
  const url = template.includes('{code}')
    ? template.replace('{code}', encodeURIComponent(code))
    : template + encodeURIComponent(code);
  countCall('resolve');
  const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (!r.ok) throw new Error(`resolve endpoint returned ${r.status}`);
  const body = await r.json();
  // Accept the obvious shapes rather than demanding one.
  const dig = o => typeof o === 'string' ? o
    : o && (o.ticket_number || o.ticket || dig(o.data) || dig(o.result));
  const ticket = dig(body);
  if (!ticket) throw new Error('no ticket_number in the resolve response');
  return String(ticket);
}

async function getPage(page, limit, status, sku) {
  const url = new URL(LIST_URL);
  url.searchParams.set('page', page);
  url.searchParams.set('limit', limit);
  if (status) url.searchParams.set('status', status);
  // The service still holds last year's DAY12025 tickets, so the SKU filter is
  // what keeps a print run to this event.
  if (sku) url.searchParams.set('sku', sku);
  // Without this the API withholds qr_string from anyone who has not yet
  // confirmed, and from family tickets. Badges get printed weeks ahead, so a
  // guest who RSVPs after the print run still needs a scannable badge.
  url.searchParams.set('showQr', 'true');
  countCall('ticket list');
  const r = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
  if (r.status === 401 || r.status === 403) {
    throw new Unauthorised('Your session expired — sign in again.');
  }
  if (!r.ok) throw new Error(`Ticket API returned ${r.status}.`);
  return r.json();
}

/* Family tickets do not appear as rows of their own. They are nested inside
   their host's `child_ticket_data`, and carry `user_category: "Family"` with a
   `parent_ticket_num` pointing back at the host. Flattening here means nothing
   downstream has to know they arrived differently. */
function flatten(row, out = []) {
  const { child_ticket_data: children, ...self } = row;
  out.push(self);
  for (const child of children || []) flatten(child, out);
  return out;
}

/* The list endpoint pages unstably: its ordering has no unique tiebreaker, so
   rows shift across page boundaries and some are served twice while others are
   never served at all. Measured against a database export of the same moment,
   500-per-page returned 2,332 rows of which 8 were repeats -- 8 real tickets,
   5 of them confirmed guests, absent from every page. 1,000 lost the same 8;
   2,000 and above, which fit the event in one or two requests, lost none.

   So ask for the lot in a single request. Every boundary is a chance to drop
   somebody, and the count is small enough that there need not be any. */
const PAGE_LIMIT = 5000;

/** Every ticket of the given status, in as few requests as the service allows. */
export async function fetchTickets({ status = 'CLAIMED', sku = SKU,
                                     limit = PAGE_LIMIT, onProgress } = {}) {
  const rows = [];
  /* Paging is not a snapshot: pulling four pages takes seconds, and a ticket
     created or confirmed in between shifts every row after it, which can hand
     back the same ticket on two pages. One duplicate inflates every figure in
     a report, so tickets are taken once and the repeats are counted rather
     than dropped in silence. */
  const seen = new Set();
  let duplicates = 0;
  let page = 1, totalPages = null, total = null, fetched = 0;
  for (;;) {
    const body = await getPage(page, limit, status, sku);
    // Belt and braces: the query is filtered, but a wrong-SKU row must never
    // reach a badge, so drop it here too.
    const batch = (body.data || []).filter(r => !sku || r.sku === sku);
    for (const row of batch) {
      for (const one of flatten(row)) {
        if (sku && one.sku !== sku) continue;
        const id = one.ticket_number || one['Ticket Number'];
        if (id) {
          if (seen.has(id)) { duplicates += 1; continue; }
          seen.add(id);
        }
        rows.push(one);
      }
    }
    if (totalPages === null) {
      totalPages = body.pagination?.total_pages ?? null;
      total = body.pagination?.total ?? null;
    }
    // Progress counts tickets fetched, not rows produced — family members are
    // extra rows that the endpoint's own total knows nothing about.
    fetched += batch.length;
    onProgress?.(fetched, total ?? fetched);
    if (totalPages !== null ? page >= totalPages : batch.length < limit) break;
    page += 1;
    if (page > 500) break;
  }
  /* The service's own count of parent tickets is the check on all of this: if
     fewer distinct ones arrived than it says exist, rows were dropped in
     transit and every figure downstream is short. Caller decides how loudly to
     say so; it must not pass unnoticed. */
  const parents = rows.filter(r => !r.parent_ticket_num).length;
  const missing = total !== null ? Math.max(0, total - parents) : 0;
  return { rows, total: total ?? rows.length, duplicates, missing, parents };
}

/** One cheap call, to read the status tallies without pulling everything. */
export async function fetchCounts() {
  const body = await getPage(1, 1, '', '');
  return body.count || {};
}
